import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { AgGridColumnDef, SelectOption } from '@tassat/common-definitions';
import React from 'react';

import { AG_GRID_SMALL_HEADER_HEIGHT, emptySelectOption } from 'Lib/constant';
import { caseInsensitiveSort } from 'Lib/stringHelpers';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAllAccountsArray } from 'Selectors/accounts';
import { getAccounts, getAlerts } from 'Selectors/common';
import { getFirmsArray } from 'Selectors/firms';
import ExternalFilter from 'SharedComponents/agGrid/filters/externalFilter';
import ServerSort from 'SharedComponents/agGrid/serverSort';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import ExternalFilterHeaderComponent from 'SharedComponents/cellRenderComponent/externalFilterHeaderComponent';
import FirmNameCellRenderer from 'SharedComponents/cellRenderComponent/firmNameCellRenderer';
import UserNameCellRenderer from 'SharedComponents/cellRenderComponent/userNameCellRenderer';
import WalletNameCellRenderer from 'SharedComponents/cellRenderComponent/walletNameCellRenderer';

import { getMappedColumn } from './alertsAgGridOptions';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';

const AlertsAgGrid = () => {
    const alerts = useAppSelector(getAlerts);
    const accounts = useAppSelector(getAccounts);
    const accountsArray = useAppSelector(getAllAccountsArray);
    const firmsArray = useAppSelector((state) => getFirmsArray(state, '', true));
    const [ gridApi, onGridReady ] = useAgGrid();

    const firmSelectOptions: SelectOption[] = React.useMemo(() => {
        firmsArray.sort((firmA, firmB) => caseInsensitiveSort(firmA.Name, firmB.Name));

        const firmOptions: SelectOption[] = firmsArray
            .map(firm => {
                return {
                    label: firm.Name,
                    value: firm.Name
                };
            });

        firmOptions.unshift(emptySelectOption);

        return firmOptions;
    }, [firmsArray]);

    const accountSelectOptions: SelectOption[] = React.useMemo(() => {
        accountsArray
            .sort((accountA, accountB) => caseInsensitiveSort(
                `${accountA.AccountName}`,
                `${accountB.AccountName}`
            ));

        const accountOptions: SelectOption[] = accountsArray
            .map(account => {
                return {
                    label: `${account.AccountName}`,
                    value: account.AccountId
                };
            });
        accountOptions.unshift(emptySelectOption);

        return accountOptions;
    }, [accountsArray]);

    const alertSelectOptions: SelectOption[] = React.useMemo(() => {
        const alertsOptions: SelectOption[] = alerts.reduce((acc, cv) => {
            if (!acc.includes(cv.AlertCode)) {
                acc.push(cv.AlertCode);
            }

            return acc;
        }, [] as string[])
            .map(code => ({
                label: code,
                value: code
            }));
        alertsOptions.unshift(emptySelectOption);

        return alertsOptions;
    }, [alerts]);

    React.useEffect(() => {
        gridApi?.api.refreshCells();
        gridApi?.api?.refreshHeader();
    }, [ gridApi?.api, accounts, firmsArray ]);

    const AlertsColumnDefs: AgGridColumnDef[] = React.useMemo(() => [
        {
            field: 'AlertTime',
            headerName: 'Date/Time',
            cellRenderer: 'dateCellRenderer',
            menuTabs: [],
            flex: 5
        },
        {
            field: 'FirmName',
            headerName: 'Firm',
            valueGetter: ({ data }) => data?.FirmName || '-',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'select',
                selectOptions: firmSelectOptions
            },
            menuTabs: ['filterMenuTab'],
            flex: 6
        },
        {
            field: 'Username',
            headerName: 'Username',
            valueGetter: ({ data }) => data?.Username || '-',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 7
        },
        {
            field: 'AccountId',
            headerName: 'Account',
            valueGetter: ({ data }) => {
                const account = accounts[data.AccountId];

                return account ? account.AccountName : '-';
            },
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 7
        },
        {
            field: 'RequesterUsername',
            headerName: 'Requester Username',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 7
        },
        {
            field: 'AlertCode',
            headerName: 'Alert Type',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'select',
                selectOptions: alertSelectOptions
            },
            menuTabs: ['filterMenuTab'],
            flex: 5
        },
        {
            field: 'AlertDescription',
            headerName: 'Alert Message',
            menuTabs: [],
            flex: 5
        },
        {
            field: 'RequestId',
            headerName: 'Reference ID',
            menuTabs: [],
            flex: 5
        }
    ], [
        accounts,
        alertSelectOptions,
        accountSelectOptions,
        firmSelectOptions
    ]);

    return (
        <div
            className='ag-theme-material'
            id='adminUsersGrid'
            style={{
                height: '100%',
                borderTop: '1px solid #BFB9B9'
            }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={AlertsColumnDefs}
                columnMenu='legacy'
                components={{
                    dateCellRenderer: DateCellRender,
                    firmNameCellRenderer: FirmNameCellRenderer,
                    userNameCellRenderer: UserNameCellRenderer,
                    walletNameCellRenderer: WalletNameCellRenderer,
                    externalFilterHeader: ExternalFilterHeaderComponent,
                    externalFilter: ExternalFilter,
                    serverSort: ServerSort
                }}
                defaultColDef={{
                    resizable: true,
                    filter: false,
                    sortable: false
                }}
                enableCellTextSelection={true}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                onGridReady={onGridReady}
                rowData={alerts}
                suppressCellFocus={true}
                suppressContextMenu={true}
            />
        </div>
    );
};

export default AlertsAgGrid;